import { useState } from "react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Download, Trash2, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone || "");
  const [examDate, setExamDate] = useState(user.exam_date || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const saveProfile = async () => {
    try {
      const r = await api.put("/auth/profile", { name, phone, exam_date: examDate || null });
      setUser(r.data);
      toast.success("Datos actualizados");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const changePw = async () => {
    try {
      await api.put("/auth/password", pw);
      toast.success("Contraseña actualizada");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportData = async () => {
    const r = await api.get("/auth/export");
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "natur-academy-mis-datos.json";
    a.click();
    toast.success("Datos exportados (RGPD)");
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/auth/account");
      toast.success("Cuenta eliminada");
      logout(); nav("/");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fade-up max-w-2xl" data-testid="profile-page">
      <h1 className="text-3xl font-semibold tracking-tight mb-1">Mi Perfil</h1>
      <p className="text-neutral-500 mb-8 flex items-center gap-1.5"><ShieldCheck size={15} className="text-green-600" /> Cumplimiento RGPD y LOPDGDD</p>

      <div className="space-y-6">
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold mb-4">Datos personales</h2>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Teléfono {user.role === "PROFESOR" && "(WhatsApp para alumnos)"}</Label>
              <Input data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" placeholder="34600000000" /></div>
            <div><Label>Email</Label><Input value={user.email} disabled className="mt-1.5 bg-neutral-50" /></div>
            {user.role === "ALUMNO" && (
              <div><Label>Fecha prevista de examen</Label>
                <Input data-testid="profile-exam-date-input" type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} className="mt-1.5" /></div>
            )}
            <Button data-testid="profile-save-btn" onClick={saveProfile} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">Guardar</Button>
          </div>
        </div>

        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold mb-4">Cambiar contraseña</h2>
          <div className="space-y-3">
            <Input data-testid="profile-current-pw" type="password" placeholder="Contraseña actual" value={pw.current_password}
                   onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
            <Input data-testid="profile-new-pw" type="password" placeholder="Nueva contraseña (mín. 6)" value={pw.new_password}
                   onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
            <Button data-testid="profile-pw-btn" onClick={changePw} disabled={pw.new_password.length < 6} variant="outline" className="rounded-full">Actualizar contraseña</Button>
          </div>
        </div>

        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold mb-4">Privacidad</h2>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="profile-export-btn" onClick={exportData} variant="outline" className="rounded-full">
              <Download size={15} className="mr-1.5" /> Exportar mis datos
            </Button>
            {user.role !== "SUPER_ADMIN" && (!confirmDelete ? (
              <Button data-testid="profile-delete-btn" onClick={() => setConfirmDelete(true)} variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 size={15} className="mr-1.5" /> Eliminar cuenta
              </Button>
            ) : (
              <Button data-testid="profile-delete-confirm-btn" onClick={deleteAccount} className="rounded-full bg-red-600 hover:bg-red-700 text-white">
                Confirmar eliminación definitiva
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
